import React, { useState, useEffect } from "react";
import { FaRegSave } from "react-icons/fa";
import { TiPencil } from "react-icons/ti";
import { Link } from "react-router-dom";
import Header from "../../../../../../header/header";
import Navvbar from "../../../../../../header/Navvbar";
import Footer from "../../../../../../footer";
import Pricing from "./Pricing";

const EditPriceLists = () => {
  const [percentage, setPercentage] = useState("");
  const [textInput, setTextInput] = useState("");
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [procedures, setProcedures] = useState([]);
  const [allProcedures, setAllProcedures] = useState([]); // Store all procedures
  const [filteredProcedures, setFilteredProcedures] = useState([]); // Store filtered procedures
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editablePrices, setEditablePrices] = useState({});
  const [saving, setSaving] = useState(false);
  const [userLabId, setUserLabId] = useState(null);
  const [showAllProcedures, setShowAllProcedures] = useState(false); // Toggle to show all

  // Get userLabId from localStorage on component mount
  useEffect(() => {
    const storedUserLabId = localStorage.getItem('userLabId');
    console.log("Retrieved userLabId from localStorage:", storedUserLabId);
    setUserLabId(storedUserLabId);
  }, []);

  // Fetch all procedures from API
  useEffect(() => {
    const fetchAllProcedures = async () => {
      setLoading(true);
      setError("");
      
      try {
        console.log("Fetching all procedures...");
        const response = await fetch("https://hms.backend.hiragroupcloud.com/api/procedures");
        
        if (!response.ok) {
          throw new Error(`Failed to fetch procedures: ${response.status}`);
        }
        
        const data = await response.json();
        console.log("API Response received");
        
        // Handle both response formats
        const proceduresData = data.data || data;
        
        if (!Array.isArray(proceduresData)) {
          throw new Error("Invalid data format received from API");
        }
        
        console.log("Total procedures fetched:", proceduresData.length);
        
        // Debug: Show all procedures with their lab IDs
        proceduresData.forEach((proc, idx) => {
          console.log(`Procedure ${idx + 1}: ${proc.name}, Lab ID: ${proc.addedBy?.userLabId || 'No lab ID'}`);
        });
        
        setAllProcedures(proceduresData);
        
      } catch (err) {
        console.error("Error fetching procedures:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAllProcedures();
  }, []);

  // Filter procedures when userLabId changes or when showAllProcedures changes
  useEffect(() => {
    if (allProcedures.length > 0) {
      filterProcedures();
    }
  }, [userLabId, allProcedures, showAllProcedures]);

  // Function to filter procedures
  const filterProcedures = () => {
    if (showAllProcedures || !userLabId) {
      // Show all procedures
      console.log("Showing all procedures");
      setFilteredProcedures(allProcedures);
      initializeEditablePrices(allProcedures);
    } else {
      // Filter by userLabId
      const filtered = allProcedures.filter(procedure => {
        const procedureLabId = procedure.addedBy?.userLabId;
        const match = procedureLabId === userLabId;
        console.log(`Checking ${procedure.name}: ${procedureLabId} === ${userLabId} = ${match}`);
        return match;
      });
      
      console.log(`Filtered ${filtered.length} procedures for lab ${userLabId}`);
      console.log("Filtered procedures:", filtered.map(p => p.name));
      
      setFilteredProcedures(filtered);
      initializeEditablePrices(filtered);
    }
  };

  // Helper function to initialize editable prices
  const initializeEditablePrices = (proceduresData) => {
    const initialEditablePrices = {};
    proceduresData.forEach(procedure => {
      procedure.prices?.forEach(price => {
        const key = getPriceKey(procedure._id, price);
        initialEditablePrices[key] = price.price ? price.price.toString() : "0.00";
      });
    });
    console.log("Initialized editable prices for", proceduresData.length, "procedures");
    setEditablePrices(initialEditablePrices);
  };

  // Helper function to generate unique key for each price
  const getPriceKey = (procedureId, price) => {
    if (price.pricingFor === "Group") {
      return `${procedureId}_Group_${price.target || 'default'}`;
    } else if (price.pricingFor === "Single Account") {
      return `${procedureId}_Single Account_${price.accountName || price.target || 'default'}`;
    } else {
      return `${procedureId}_${price.pricingFor}_${price.target || 'default'}`;
    }
  };

  const handleAlterPrices = () => {
    if (percentage) {
      // Apply percentage change to all editable prices
      const updatedPrices = { ...editablePrices };
      Object.keys(updatedPrices).forEach(key => {
        const currentValue = parseFloat(updatedPrices[key]);
        if (!isNaN(currentValue)) {
          const changePercentage = parseFloat(percentage) / 100;
          const newValue = currentValue * (1 + changePercentage);
          updatedPrices[key] = newValue.toFixed(2);
        }
      });
      setEditablePrices(updatedPrices);
      alert(`Prices altered by ${percentage}%`);
    }
  };

  const handleProcedureClick = (procedure) => {
    sessionStorage.setItem('procedureId', procedure._id);
    sessionStorage.setItem('procedureName', procedure.name);
    setSelectedProcedure(procedure);
  };

  const handleBackToPriceLists = () => {
    setSelectedProcedure(null);
    sessionStorage.removeItem('procedureId');
    sessionStorage.removeItem('procedureName');
  };

  // Handle price input change
  const handlePriceChange = (procedureId, price, value) => {
    const key = getPriceKey(procedureId, price);
    setEditablePrices(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Get group name based on pricing type
  const getGroupName = (price) => {
    if (price.pricingFor === "Group") {
      return price.target || "";
    }
    return "";
  };

  // Get account name based on pricing type
  const getAccountName = (price) => {
    if (price.pricingFor === "Single Account") {
      return price.accountName || price.target || "";
    }
    return "";
  };

  // Toggle between filtered and all procedures
  const toggleShowAllProcedures = () => {
    setShowAllProcedures(!showAllProcedures);
  };

  // Save all prices to backend using universal API
  const handleSaveAllPrices = async () => {
    setSaving(true);
    try {
      const updates = [];

      // Collect all price updates for both Groups and Single Accounts
      filteredProcedures.forEach(procedure => {
        procedure.prices?.forEach(price => {
          const key = getPriceKey(procedure._id, price);
          const newPrice = editablePrices[key];

          if (newPrice && !isNaN(parseFloat(newPrice))) {
            const originalPrice = price.price || 0;
            const updatedPrice = parseFloat(newPrice);

            if (updatedPrice !== originalPrice) {
              const targetName = price.pricingFor === "Group" ? price.target : (price.accountName || price.target);

              updates.push({
                procedureId: procedure._id,
                pricingType: price.pricingFor,
                target: targetName || "default",
                price: updatedPrice
              });
            }
          }
        });
      });

      if (updates.length === 0) {
        alert("No changes to save");
        setSaving(false);
        return;
      }

      console.log("Saving updates:", updates);

      const response = await fetch("https://hms.backend.hiragroupcloud.com/api/prices/update-prices", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ updates }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to save prices");
      }

      const result = await response.json();
      alert(`Successfully updated ${result.updated} prices`);

      // Refresh procedures
      const refreshResponse = await fetch("https://hms.backend.hiragroupcloud.com/api/procedures");
      if (refreshResponse.ok) {
        const refreshedData = await refreshResponse.json();
        const proceduresData = refreshedData.data || refreshedData;
        setAllProcedures(proceduresData);
        // Re-apply filtering
        filterProcedures();
      }

    } catch (err) {
      console.error("Error saving prices:", err);
      alert("Failed to save prices: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Display loading while fetching
  if (loading) {
    return (
      <>
        <div style={{ marginBottom: "25px" }}>
          <Header />
          <Navvbar />
        </div>
        <div style={{ width: "95%", margin: "0 auto", textAlign: "center", padding: "50px" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Loading procedures...</p>
        </div>
      </>
    );
  }

  // If a procedure is selected, show the Pricing component
  if (selectedProcedure) {
    return (
      <>
        <div style={{ marginBottom: "25px" }}>
          <Header />
          <Navvbar />
        </div>

        <div style={{ width: "95%", margin: "0 auto" }}>
          <button
            onClick={handleBackToPriceLists}
            style={{
              backgroundColor: "#004aad",
              color: "white",
              border: "none",
              padding: "10px 20px",
              borderRadius: "4px",
              cursor: "pointer",
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              gap: "5px"
            }}
          >
            ← Back to Price Lists
          </button>
          <div style={{
            backgroundColor: "white",
            padding: "20px",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
          }}>
            <h2 style={{ color: "#004aad", marginBottom: "20px" }}>
              Pricing for: {selectedProcedure.name}
            </h2>
            <Pricing />
          </div>
        </div>

        <div style={{ width: "95%", margin: "0 auto" }}>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <div style={{ marginBottom: "25px" }}>
        <Header />
        <Navvbar />
      </div>

      <div style={{ width: "95%", margin: "0 auto" }}>
        <div className="row">
          {/* Sidebar */}
          <div className="col-md-2" style={{ borderRight: "1px solid #004aad" }}>
            <h2 style={{ marginLeft: "10px" }}>Settings</h2>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                marginLeft: "12px",
              }}
            >
              {[
                { text: "Lab Details", link: "/Tabs" },
                { text: "Store", link: "/" },
                { text: "Users", link: "/usersetting" },
                { text: "Permission Groups", link: "/" },
                { text: "Accounts", link: "/Account" },
                { text: "Finances", link: "/Owerviews" },
                { text: "Case Settings", link: "/owersetting" },
              ].map((item, idx) => (
                <Link
                  key={idx}
                  to={item.link}
                  style={{
                    textDecoration: "none",
                    fontSize: "17px",
                    color: "#004aad",
                  }}
                >
                  {item.text}
                </Link>
              ))}
            </div>
          </div>

          {/* Main Content */}
          <div className="col-md-10">
            <h1 style={{ marginBottom: "10px" }}>Edit Price Lists</h1>
            {/* Tools Section */}
            <h2 style={{ color: "#004aad", marginBottom: "10px" }}>Edit Tools</h2>
            <div
              style={{
                backgroundColor: "#f8f9fa",
                padding: "15px",
                border: "1px solid #ccc",
                borderTop: "2px solid #004aad",
                marginBottom: "20px",
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <label style={{ fontWeight: "500" }}>Alter Prices By</label>
              <input
                type="text"
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                placeholder="Optional note"
                style={{
                  padding: "6px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  width: "120px",
                }}
              />
              <select
                value={percentage}
                onChange={(e) => setPercentage(e.target.value)}
                style={{
                  padding: "6px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  width: "80px",
                }}
              >
                <option value="">Select %</option>
                <option value="5">5%</option>
                <option value="10">10%</option>
                <option value="15">15%</option>
                <option value="20">20%</option>
                <option value="-5">-5%</option>
                <option value="-10">-10%</option>
                <option value="-15">-15%</option>
                <option value="-20">-20%</option>
              </select>
              <button
                onClick={handleAlterPrices}
                style={{
                  backgroundColor: "#004aad",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  padding: "8px 15px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                <TiPencil /> Alter Prices
              </button>
            </div>

            {/* Error State */}
            {error && (
              <div style={{ 
                color: "#721c24", 
                backgroundColor: "#f8d7da",
                border: "1px solid #f5c6cb",
                borderRadius: "4px",
                padding: "15px",
                marginBottom: "20px"
              }}>
                <strong>Error:</strong> {error}
                <div style={{ marginTop: "10px" }}>
                  <button 
                    onClick={() => window.location.reload()}
                    style={{
                      backgroundColor: "#dc3545",
                      color: "white",
                      border: "none",
                      padding: "5px 10px",
                      borderRadius: "4px",
                      cursor: "pointer"
                    }}
                  >
                    Retry
                  </button>
                </div>
              </div>
            )}

            {/* Price List Sections */}
            <h3 style={{ color: "#004aad", marginBottom: "10px" }}>
              Price Lists By Procedure
              {filteredProcedures.length > 0 && (
                <span style={{ 
                  fontSize: "14px", 
                  fontWeight: "normal",
                  marginLeft: "10px",
                  color: "#666"
                }}>
                  ({filteredProcedures.length} procedures found)
                </span>
              )}
            </h3>

            {filteredProcedures.length === 0 && !error ? (
              <div style={{ 
                textAlign: "center", 
                padding: "40px",
                backgroundColor: "#f8f9fa",
                borderRadius: "4px",
                border: "1px dashed #ccc",
                marginBottom: "20px"
              }}>
                <h5>No procedures found</h5>
                {userLabId && !showAllProcedures ? (
                  <>
                    <p style={{ color: "#666", fontSize: "14px" }}>
                      No procedures are associated with your lab ID: <strong>{userLabId}</strong>
                    </p>
                    <div style={{ marginTop: "20px" }}>
                      <button
                        onClick={toggleShowAllProcedures}
                        style={{
                          backgroundColor: "#004aad",
                          color: "white",
                          border: "none",
                          padding: "8px 16px",
                          borderRadius: "4px",
                          cursor: "pointer"
                        }}
                      >
                        Show All Procedures Instead
                      </button>
                    </div>
                  </>
                ) : (
                  <p style={{ color: "#666", fontSize: "14px" }}>
                    No procedures available in the system.
                  </p>
                )}
              </div>
            ) : null}

            {filteredProcedures.map((procedure, index) => (
              <div key={procedure._id} style={{ marginBottom: "25px" }}>
                {/* Procedure Header - Clickable */}
                <div
                  style={{
                    backgroundColor: "#f4f4f4",
                    padding: "10px",
                    fontWeight: "bold",
                    color: "#004aad",
                    borderTop: index === 0 ? "2px solid #004aad" : "1px solid #ccc",
                    cursor: "pointer",
                    transition: "all 0.3s",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}
                  onClick={() => handleProcedureClick(procedure)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#e8e8e8";
                    e.currentTarget.style.color = "black";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#f4f4f4";
                    e.currentTarget.style.color = "#004aad";
                  }}
                >
                  <span>
                    {procedure.name}
                    {procedure.category && (
                      <span style={{ 
                        fontSize: "12px", 
                        fontWeight: "normal",
                        marginLeft: "10px",
                        color: "#666"
                      }}>
                        ({procedure.category})
                      </span>
                    )}
                  </span>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    {procedure.addedBy?.userLabId && (
                      <span style={{ 
                        fontSize: "11px", 
                        fontWeight: "normal",
                        color: procedure.addedBy.userLabId === userLabId ? "#28a745" : "#dc3545",
                        backgroundColor: procedure.addedBy.userLabId === userLabId ? "#d4edda" : "#f8d7da",
                        padding: "2px 6px",
                        borderRadius: "3px"
                      }}>
                        Lab: {procedure.addedBy.userLabId}
                        {procedure.addedBy.userLabId === userLabId ? " ✓" : " ✗"}
                      </span>
                    )}
                    <span style={{ 
                      fontSize: "11px", 
                      color: "#6c757d"
                    }}>
                      {procedure.prices?.length || 0} price(s)
                    </span>
                  </div>
                </div>

                {/* Column Header */}
                <div
                  style={{
                    backgroundColor: "#f4f4f4",
                    display: "flex",
                    fontWeight: "bold",
                    fontSize: "14px",
                    padding: "8px 10px",
                  }}
                >
                  <div style={{ width: "25%" }}>Group</div>
                  <div style={{ width: "25%" }}>Account</div>
                  <div style={{ width: "25%" }}>Attributes</div>
                  <div style={{ width: "25%", textAlign: "right" }}>New Price</div>
                </div>

                {/* Price Rows */}
                {procedure.prices && procedure.prices.length > 0 ? (
                  procedure.prices.map((price, priceIndex) => {
                    const key = getPriceKey(procedure._id, price);
                    const currentPrice = editablePrices[key] || (price.price ? price.price.toString() : "0.00");
                    const groupName = getGroupName(price);
                    const accountName = getAccountName(price);

                    return (
                      <div
                        key={price._id || priceIndex}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "10px 10px",
                          borderBottom: priceIndex === procedure.prices.length - 1
                            ? "none"
                            : "1px solid #eee",
                          fontSize: "14px",
                          backgroundColor: priceIndex % 2 === 0 ? "#fff" : "#f9f9f9"
                        }}
                      >
                        <div style={{ width: "25%" }}>
                          {groupName || "-"}
                        </div>
                        <div style={{ width: "25%" }}>
                          {accountName || "-"}
                        </div>
                        <div style={{ width: "25%" }}>
                          <span style={{ 
                            fontSize: "12px", 
                            color: "#666",
                            backgroundColor: "#f0f0f0",
                            padding: "2px 6px",
                            borderRadius: "3px"
                          }}>
                            {price.pricingFor}
                          </span>
                        </div>
                        <div style={{ width: "25%", textAlign: "right" }}>
                          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "5px" }}>
                            <span style={{ fontSize: "12px", color: "#666" }}>$</span>
                            <input
                              type="text"
                              value={currentPrice}
                              onChange={(e) => handlePriceChange(
                                procedure._id,
                                price,
                                e.target.value
                              )}
                              style={{
                                width: "80px",
                                textAlign: "right",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                padding: "3px 5px",
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "10px 10px",
                      fontSize: "14px",
                      color: "#666",
                      backgroundColor: "#fff"
                    }}
                  >
                    <div style={{ width: "100%", textAlign: "center" }}>
                      No prices configured for this procedure
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Save Button - Only show if there are procedures */}
            {!error && filteredProcedures.length > 0 && (
              <div style={{ 
                clear: "both", 
                paddingTop: "20px",
                borderTop: "1px solid #eee",
                marginTop: "20px"
              }}>
              
                <button
                  onClick={handleSaveAllPrices}
                  disabled={saving}
                  style={{
                    backgroundColor: saving ? "#6c757d" : "#004aad",
                    color: "white",
                    padding: "10px 20px",
                    border: "none",
                    borderRadius: "4px",
                    cursor: saving ? "not-allowed" : "pointer",
                    float: "right",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    opacity: saving ? 0.6 : 1,
                  }}
                >
                  <FaRegSave /> {saving ? "Saving..." : "Save All Changes"}
                </button>
                <div style={{ clear: "both" }}></div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ width: "95%", margin: "0 auto", marginTop: "40px" }}>
        <Footer />
      </div>
    </>
  );
};

export default EditPriceLists;